// src/pages/Creatives.jsx
import React from 'react';
import { Box, Typography, Button, Container, Grid, Card, CardContent, Link } from '@mui/material';

const brandYellow = '#FCCB26';

// #ffc500

// #f6cb38

// #f5dc87

// #fff7da

const Section = ({ title, children, bg, color }) => (
    <Box py={8} bgcolor={bg || 'white'} color={color || 'black'}>
        <Container>
            {title && (
                <Typography variant="h4" fontWeight="bold" mb={4} textAlign="center">
                    {title}
                </Typography>
            )}
            {children}
        </Container>
    </Box>
);

const Supports = () => {
    return (
        <Box>

            {/* Hero Section with Background Image and Blur */}
            <Box
                sx={{
                    position: 'relative',
                    height: '95vh',
                    color: 'white',
                    overflow: 'hidden',
                }}
            >
                {/* Background Image */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'url(/assets/servicesPic/supports.jpg)', // ← Replace with your actual image
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'brightness(0.5) blur(2px)',
                        zIndex: 0,
                    }}
                />

                {/* Content Over Background */}
                <Box
                    sx={{
                        position: 'relative',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        zIndex: 1,
                        textAlign: 'center',
                        px: 3,
                    }}
                >
                    <Typography variant="h2" fontWeight="bold" mb={2}>
                        Higher Low Support Services
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#FCCB26' }} gutterBottom>
                        Plug-In Support for Productions at Any Stage.
                    </Typography>
                    <Typography variant="body1" maxWidth="md" fontSize={20}>
                        A System That Fills the Gaps — Efficiently and Professionally. Whether you’re producing solo, with a lean crew, or in the thick of a larger production, Higher Low provides dependable, production-ready support that adapts to your project’s unique needs. We cover the critical areas that often slow teams down — with speed, clarity, and care.
                    </Typography>
                </Box>
            </Box>



            {/* What We Do Section */}
            <Section title="What We Offer Producers, Creatives & Indie Teams" bg="#fff7da">
                <Grid container spacing={5}>
                    {[
                        ['Production Insurance (Short-Term or Full Project)', 'Need a policy to protect your set or secure locations? We provide quick-turnaround coverage solutions.'],
                        ['Payroll & Contractor Management', 'Let us handle the hiring paperwork, timecards, payments, and compliance — all legit and seamless.'],
                        ['Post-Production Services', 'Editing, color, sound design, music licensing, delivery — we help you cross the finish line with polish.'],
                        ['Production Support & Staffing', 'On-call producers, coordinators, PAs, and experienced crew ready to plug in when needed.'],
                        ['Gear & Rentals Access', 'Cameras, lighting, grip, sound — reliable rentals with or without crew.'],
                        ['Creative Supervision or Direction', 'Need help aligning the final product to your vision? We offer light-touch or full-on creative guidance.'],
                        ['Visual Materials & Decks', 'Support with pitch decks, mood boards, and branded visual assets for presentations or funding rounds.'],
                        ['On-Call Project Advisory', 'Don’t know where to start or feeling stuck? We’ll jump on a call and help structure your path forward.']
                    ]
                        .map(([title, desc], idx) => (
                            <Grid item xs={12} md={6} key={idx}>
                                <Box p={3} bgcolor="#d3a300" borderRadius={2} boxShadow={2}>
                                    <Typography variant="h6" fontWeight="bold">{title}</Typography>
                                    <Typography variant="body2">{desc}</Typography>
                                </Box>
                            </Grid>
                        ))}
                </Grid>
            </Section>

            {/* How It Works Section */}
            <Section title="How It Works" bg="#f6cb38">
                <Grid container spacing={4}>
                    {[
                        ['Tell Us What You Need', 'Whether you’re producing for a client or yourself, let us know what stage you’re in and what’s missing.'],
                        ['We Fill in the Blanks', 'We come in as a production partner or a solo service provider — flexible and precise.'],
                        ['Keep It Moving', "Focus on the creative. We’ll make sure the business and logistics side doesn’t slow you down."]
                    ].map(([title, desc], idx) => (
                        <Grid item xs={12} md={4} key={idx}>
                            <Box p={2} bgcolor="#f5dc87" borderRadius={2} boxShadow={1}>
                                <Typography variant="h6" fontWeight="bold">{title}</Typography>
                                <Typography variant="body2">{desc}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Section>

            {/* Why Higher Low Section (Improved) */}
            <Section title="Why Higher Low?" bg="#f5dc87">
                <Grid container spacing={3}>
                    {[
                        'Trusted infrastructure for small-to-midsize teams',
                        'Transparent, flexible pricing for individuals and productions',
                        'Built-in industry knowledge with zero gatekeeping',
                        'Fast, communicative, and genuinely invested in helping you finish strong'
                    ].map((text, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card elevation={7} sx={{ height: '100%', borderRadius: 2, backgroundColor: '#f6cb38' }}>
                                <CardContent>
                                    <Typography variant="body1" fontWeight="medium">
                                        {text}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Section>

            {/* Call To Action Section (Centered + Popped) */}
            <Section bg="#fff7da">
                <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" mb={3}>
                        Get Plugged In
                    </Typography>
                    <Typography variant="h6" mb={4}>
                        Need coverage? Need crew? Need guidance? We’re ready.
                    </Typography>
                    <Box display="flex" justifyContent="center" gap={3} flexWrap="wrap" mb={4}>
                        <Button
                            component="a"
                            href="mailto:shahrooz@higherlowfilms.com?subject=Project Support Request"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            size="large"
                            sx={{
                                bgcolor: brandYellow,
                                color: 'black',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                boxShadow: 4,
                                '&:hover': { bgcolor: '#e0b821' },
                            }}
                        >
                            Submit a Support Request
                        </Button>

                        <Button
                            component="a"
                            href="https://calendly.com/higherlowfilms/30min"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="large"
                            sx={{
                                borderColor: brandYellow,
                                color: brandYellow,
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                boxShadow: 2,
                                '&:hover': { borderColor: '#e0b821', color: '#e0b821' },
                            }}
                        >
                            Book a Quick Discovery Call
                        </Button>
                    </Box>

                    <Typography variant="body1">
                    👀 Need different support? View how we serve {' '}
                        <Link href="/services/studios" underline="hover" fontWeight="bold">
                            Studios
                        </Link>{' '}
                        –{' '}
                        <Link href="/services/brands" underline="hover" fontWeight="bold">
                            Brands
                        </Link>{' '}
                        –{' '}
                        <Link href="/services/creatives" underline="hover" fontWeight="bold">
                        Creatives
                        </Link>{' '}
                        →
                    </Typography>


                </Box>
                <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 4,
                    flexWrap: 'wrap',
                    mt: 6,
                    mb: 8, // space from footer
                    px: 2,
                }}
            >
                <Box
                    sx={{
                        width: 500,
                        height: 360,
                        borderRadius: 4,
                        overflow: 'hidden',
                        boxShadow: 3,
                        backgroundColor: '#eee',
                        backgroundImage: 'url(/assets/servicesPic/supports1.jpg)', // Replace with actual image
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />

                <Box
                    sx={{
                        width: 500,
                        height: 360,
                        borderRadius: 4,
                        overflow: 'hidden',
                        boxShadow: 3,
                        backgroundColor: '#eee',
                        backgroundImage: 'url(/assets/servicesPic/supports2.jpg)', // Replace with actual image
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
            </Box>
            </Section>

            



        </Box>


    );
};

export default Supports;
